import { createContext, useContext, useState, useMemo } from 'react';

const DashboardContext = createContext(null);

/**
 * geographyOverride shapes:
 *   Mode 1 (single county): { mode: 'single', state, county, label }
 *   Mode 2 (county group):  { mode: 'group',  state, counties: [{ fips, name }], label }
 *   Mode 3 (tract select):  { mode: 'tracts', state, county, label, tractGeoids: [...] }
 */
export function DashboardProvider({ config, children }) {
  const [geographyOverride, setGeographyOverrideState] = useState(() => {
    try {
      const stored = localStorage.getItem('fcp_geo_override');
      if (stored) return JSON.parse(stored);
      // Migrate old single-county format
      const old = localStorage.getItem('fcp_home_county');
      if (old) {
        const { county, label } = JSON.parse(old);
        const override = { mode: 'single', state: config.org.geography.state, county, label };
        localStorage.setItem('fcp_geo_override', JSON.stringify(override));
        localStorage.removeItem('fcp_home_county');
        return override;
      }
    } catch (_) {}
    return null;
  });

  const effectiveConfig = useMemo(() => {
    if (!geographyOverride) return config;
    const { mode } = geographyOverride;
    if (mode === 'single') {
      return {
        ...config,
        org: {
          ...config.org,
          geography: {
            ...config.org.geography,
            state: geographyOverride.state,
            county: geographyOverride.county,
            label: geographyOverride.label
          }
        }
      };
    }
    if (mode === 'group') {
      // Use first county as primary for TIGER bounds; state-level data still works
      const primary = geographyOverride.counties[0];
      return {
        ...config,
        org: {
          ...config.org,
          geography: {
            ...config.org.geography,
            state: geographyOverride.state,
            county: primary?.fips ?? config.org.geography.county,
            label: geographyOverride.label
          }
        }
      };
    }
    if (mode === 'tracts') {
      return {
        ...config,
        org: {
          ...config.org,
          geography: {
            ...config.org.geography,
            state: geographyOverride.state,
            county: geographyOverride.county,
            label: geographyOverride.label
          }
        }
      };
    }
    return config;
  }, [config, geographyOverride]);

  function setGeographyOverride(override) {
    localStorage.setItem('fcp_geo_override', JSON.stringify(override));
    setGeographyOverrideState(override);
  }

  return (
    <DashboardContext.Provider value={{ config: effectiveConfig, geographyOverride, setGeographyOverride }}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboardConfig() {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useDashboardConfig must be used within a DashboardProvider');
  return ctx.config;
}

export function useGeographyOverride() {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useGeographyOverride must be used within a DashboardProvider');
  return ctx.geographyOverride;
}

export function useSetGeographyOverride() {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useSetGeographyOverride must be used within a DashboardProvider');
  return ctx.setGeographyOverride;
}
